import React, { useEffect, useState } from "react";
import { Card, Row, Col, Spin, message, Table, Avatar, Tag } from "antd";
import { getRequest } from "../api/api";

// Icons
// import { MdOutlineCategory } from "react-icons/md";
import { SiBookstack } from "react-icons/si";
import { GiBookshelf } from "react-icons/gi";
// import { BiMessageDetail } from "react-icons/bi";
import { SlLike } from "react-icons/sl";
import { FaUsersLine } from "react-icons/fa6";

// import { RxDashboard } from "react-icons/rx";
import { MdFeedback } from "react-icons/md";
import {  GiBlackBook } from "react-icons/gi";
// import { RxDropdownMenu } from "react-icons/rx";
import { RxDashboard } from "react-icons/rx";
import { MdOutlineFolderSpecial } from "react-icons/md";
// Charts
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
  PieChart,
  Pie,
  Cell,
} from "recharts";

/* -------------------------- helpers & UI bits -------------------------- */
const numberize = (v) =>
  typeof v === "number"
    ? v.toLocaleString()
    : (Number(v) || 0).toLocaleString();

const cardBG =
  "linear-gradient(0deg, #E0C2E0 80.26%, rgba(250, 245, 250, 0) 143.39%, #F5EBF5 143.39%)";

const StatCard = ({
  title,
  value,
  Icon,
  gradient,
  meta = [],
  footerLabel = "Total",
}) => (
  <Card
    
    style={{
      borderRadius: 16,
      boxShadow: "0 8px 20px rgba(0,0,0,0.06)",
      overflow: "hidden",
      position: "relative",
      background: cardBG,
      border: "1px solid rgba(0,0,0,0.04)",
    }}
    bodyStyle={{ padding: 18 }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
      }}
    >
      <div>
        <div style={{ color: "#4b5563", fontWeight: 600 }}>{title}</div>
        <div
          className="ds-number-total"
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "baseline",
            gap: "10px",
          }}
        >
          <div style={{ fontSize: 28, fontWeight: 800, marginTop: 8 }}>
            {numberize(value)}
          </div>

          <div style={{ color: "#6b7280", fontSize: 12, marginTop: 6 }}>
            {footerLabel}
          </div>
        </div>
        {/* small pills row */}
        {meta.length > 0 && (
          <div
            style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}
          >
            {meta.map((m, i) => (
              <span
                key={i}
                style={{
                  fontSize: 12,
                  padding: "4px 8px",
                  borderRadius: 999,
                  background: m.bgColor ?? "rgb(209, 250, 229)",
                  color: m.color ?? "rgb(6, 95, 70)",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  lineHeight: 1,
                }}
              >
                <strong style={{ fontWeight: 700 }}>
                  {numberize(m.value)}
                </strong>
                <span style={{ opacity: 0.9 }}>{m.label}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 12,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: gradient,
          color: "#fff",
          flexShrink: 0,
        }}
      >
        <Icon size={20} />
      </div>
    </div>

    {/* decorative wave */}
    <svg
      viewBox="0 0 200 80"
      width="160"
      height="80"
      style={{ position: "absolute", right: -8, bottom: -6, opacity: 0.18 }}
    >
      <path
        d="M0,60 C40,20 80,100 120,40 C150,10 180,60 210,30 L210,90 L0,90 Z"
        fill="url(#grad)"
      />
      <defs>
        <linearGradient id="grad" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="rgb(124, 58, 237)" />
          <stop offset="100%" stopColor="rgb(244, 63, 94)" />
        </linearGradient>
      </defs>
    </svg>
  </Card>
);

/* --------------------------------- page -------------------------------- */
const Dashboard = () => {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({ counts: null, users: null });

  // NEW: leaderboard state
  const [lbLoading, setLbLoading] = useState(false);
  const [topModules, setTopModules] = useState([]);
  const [topUsers, setTopUsers] = useState([]);

  // REMOVE fetchSummary and fetchLeaderboards, and add this:
  const fetchAll = async () => {
    setLoading(true);
    setLbLoading(true);
    try {
      const res = await getRequest("dashboard/summary?active=0");
      const payload = res?.data ?? res ?? {};

      // counts + users for existing cards/charts
      const counts = payload?.counts || {};
      const users = payload?.users || {}; // keep if you read it elsewhere
      setSummary({ counts, users });

      // leaderboards
      const modulesRaw = payload?.leaderboards?.top_liked_modules ?? [];
      const usersRaw = payload?.leaderboards?.top_users_by_correct ?? [];

      const modules = normalizeModules(modulesRaw)
        .sort((a, b) => b.likes - a.likes)
        .slice(0, 10);

      const topUsersNorm = normalizeUsers(usersRaw)
        .sort((a, b) => b.score - a.score)
        .slice(0, 10);

      setTopModules(modules);
      setTopUsers(topUsersNorm);
    } catch (e) {
      console.error(e);
      message.error("Failed to load dashboard");
      setTopModules([]);
      setTopUsers([]);
      setSummary({ counts: {}, users: {} });
    } finally {
      setLoading(false);
      setLbLoading(false);
    }
  };

  // NEW: normalize helpers to be resilient to backend keys
  // map /leaderboards.top_liked_modules [{ module_id, module_name, likes }]
  // modules: [{ module_id, module_name, likes }]
  const normalizeModules = (arr) =>
    (Array.isArray(arr) ? arr : []).map((m) => {
      const id =
        m.module_id ??
        m.id ??
        m.moduleId ??
        m._id ??
        Math.random().toString(36).slice(2);
      const name =
        m.module_name ??
        m.title ??
        m.name ??
        m.module_title ??
        m.moduleName ??
        `Module #${id}`;
      const likes = Number(
        m.likes ?? m.total_likes ?? m.like_count ?? m.likes_count ?? 0
      );
      return { key: id, id, name, likes, raw: m };
    });

  // users: [{ user_id, full_name, correct_attempts, total_score, ... }]
  const normalizeUsers = (arr) =>
    (Array.isArray(arr) ? arr : []).map((u) => {
      const id =
        u.user_id ??
        u.id ??
        u.userId ??
        u._id ??
        Math.random().toString(36).slice(2);
      const name =
        u.full_name ||
        [u.first_name, u.last_name].filter(Boolean).join(" ") ||
        u.username ||
        u.email ||
        `User #${id}`;
      // show TOTAL SCORE (prefer this) and fall back safely
      const score = Number(u.total_score ?? u.correct_attempts ?? u.score ?? 0);
      const avatar = u.profile_image_url ?? u.avatar_url ?? null;
      const role = u.student_or_practitioner ?? u.role ?? null;
      return { key: id, id, name, score, avatar, role, raw: u };
    });

  const initials = (name = "") =>
    name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase())
      .join("") || "U";

  const prettyRole = (r) => {
    const s = String(r || "")
      .trim()
      .toLowerCase();
    if (!s) return null;
    if (["student"].includes(s)) return "Student";
    if (["practitioner", "doctor", "therapist"].includes(s))
      return "Practitioner";
    if (["admin", "administrator"].includes(s)) return "Admin";
    if (["moderator", "mod"].includes(s)) return "Moderator";
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const roleColor = (r) => {
    const s = String(r || "")
      .trim()
      .toLowerCase();
    if (["student"].includes(s)) return "blue";
    if (["practitioner", "doctor", "therapist"].includes(s)) return "green";
    if (["admin", "administrator"].includes(s)) return "magenta";
    if (["moderator", "mod"].includes(s)) return "geekblue";
    return "default";
  };

  // single call; data is at res.leaderboards.*

  useEffect(() => {
    fetchAll(); // single call
  }, []);

  const counts = summary?.counts || {};
  const u = counts?.users || {};

  const cards = [
    {
      title: "Modules",
      value: counts.modules?.total ?? 0,
      Icon: GiBookshelf,
      gradient: "linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)",
      meta: [
        { label: "added this month", value: counts.modules?.new_this_month ?? 0 },
      ],
    },
    {
      title: "Submodules",
      value: counts.submodules?.total ?? 0,
      Icon: GiBlackBook,
      gradient: "linear-gradient(135deg, #ec4899 0%, #f472b6 100%)",
      meta: [
        {
          label: "added this month",
          value: counts.submodules?.new_this_month ?? 0,
        },
      ],
    },
    {
      title: "Chapters",
      value: counts.chapters?.total ?? 0,
      Icon: SiBookstack,
      gradient: "linear-gradient(135deg, #f43f5e 0%, #fb7185 100%)",
      meta: [
        {
          label: "added this month",
          value: counts.chapters?.new_this_month ?? 0,
        },
      ],
    },
        {
      title: "Chapter Slides",
      value: counts.chapter_slides?.total ?? 0,
      Icon: MdOutlineFolderSpecial,
      gradient: "linear-gradient(135deg, #f43f5e 0%, #fb7185 100%)",
      meta: [
        {
          label: "added this month",  
          value: counts.chapter_slides?.new_this_month ?? 0,
        },
      ],
    },
    {
      title: "Feedback",
      value: counts.feedback?.total ?? 0,
      Icon: MdFeedback,
      gradient: "linear-gradient(135deg, #fb923c 0%, #fdba74 100%)",
      meta: [
        {
          label: "read",
          value: counts.feedback?.read ?? 0,
          color: "#065f46",
          bgColor: "#d1fae5",
        },
        {
          label: "unread",
          value: counts.feedback?.unread ?? 0,
          color: "#991b1b",
          bgColor: "#fee2e2",
        },
      ],
    },
  ];

  /* ------------------------------ chart data ------------------------------ */
const dataStatus = [
  { name: `Active (${u.active_flag_Y || 0})`, value: u.active_flag_Y || 0, color: "#3e1f3e" },
  { name: `Blocked (${u.blocked_flag_B || 0})`, value: u.blocked_flag_B || 0, color: "red" },
  { name: `Deleted (${u.deleted_flag_D || 0})`, value: u.deleted_flag_D || 0, color: "black" },
  { name: `Deactivated (${u.deactivated_flag_N || 0})`, value: u.deactivated_flag_N || 0, color: "gray" },
];


const dataLast6 = [
  {
    name: `Last 6 Months (Active: ${u.active_last_6_months || 0}, Inactive: ${u.inactive_last_6_months || 0})`,
    Active: u.active_last_6_months || 0,
    Inactive: u.inactive_last_6_months || 0,
  },
];

  const dataNewUsers = [
    {
      name: "New Users",
      "This Month": u.new_users_this_month || 0,
      "Previous Month": u.new_users_previous_month || 0,
    },
  ];

  // NEW: table columns
  const moduleCols = [
    {
      title: "#",
      width: 60,
      align: "center",
      render: (_t, _r, index) => index + 1,
    },
    {
      title: "Module",
      dataIndex: "name",
      ellipsis: true,
      render: (text) => <span style={{ fontWeight: 600 }}>{text}</span>,
    },
    {
      title: "Likes",
      dataIndex: "likes",
      align: "right",
      render: (v) => <span style={{ fontWeight: 600 }}>{numberize(v)}</span>,
    },
  ];
  const userCols = [
    {
      title: "#",
      width: 60,
      align: "center",
      render: (_t, _r, index) => index + 1,
    },
{
  title: "User",
  dataIndex: "name",
  ellipsis: true,
  render: (_name, record) => {
    const avatarSrc =
      record.avatar && record.avatar.trim() !== ""
        ? record.avatar
        : "/user-icon.svg";

    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Avatar
          src={avatarSrc}
          size={30}
          onError={() => false} // 👈 fallback triggered if image fails
        >
          <img src="/user-icon.svg" alt="user" />
        </Avatar>
        <span style={{ fontWeight: 600 }}>{record.name}</span>
      </div>
    );
  },
},

    {
      title: "Role",
      dataIndex: "role",
      width: 140,
      render: (r) => {
        const nice = prettyRole(r);
        return nice ? <Tag color={roleColor(nice)}>{nice}</Tag> : "—";
      },
    },
    {
      title: "Coin",
      dataIndex: "score",
      align: "right",
      render: (v) => <span style={{ fontWeight: 600 }}>{numberize(v)}</span>,
    },
  ];

  return (
    <div style={{ padding: 20 }}>
      <h2 style={{ marginBottom: 16 ,color: "#5a355a", display: "flex" , alignItems:"center",gap:"10px"}} > <RxDashboard/> {""}Dashboard</h2>

      <Spin spinning={loading}>
        {/* Stat cards */}
        {/* Stat cards – 5 per row on xl, 4 on lg, 3 on md, 2 on sm, 1 on xs */}
<Row gutter={[16, 16]}>
  {cards.map((c, i) => (
    <Col
      key={i}
      xs={{ flex: "0 0 100%" }}        // 1 per row
      sm={{ flex: "0 0 50%" }}         // 2 per row
      md={{ flex: "0 0 33.3333%" }}    // 3 per row
      lg={{ flex: "0 0 25%" }}         // 4 per row
      xl={{ flex: "0 0 20%" }}         // 5 per row
      style={{ minWidth: 220 }}        // keep a sensible minimum width
    >
      <StatCard {...c} />
    </Col>
  ))}
</Row>

        {/* Charts */}
        <Row gutter={[16, 16]} style={{ marginTop: 40 }}>
          {/* Pie: Status Breakdown */}
          <Col xs={24} lg={8}>
            <Card
              title="Users Statistics"
              style={{
                borderRadius: 16,
                background: "linear-gradient(0deg, rgb(238 209 238 / 54%) 80.26%, rgba(250, 245, 250, 0) 143.39%, rgb(245 235 245 / 44%) 143.39%)",
                border: "1px solid rgba(0,0,0,0.04)",
              }}
              bodyStyle={{ height: 300 }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Tooltip />
                  <Legend />
                  <Pie
                    data={dataStatus}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={55}
                    outerRadius={90}
                    paddingAngle={4}
                    isAnimationActive={true}
                  >
                    {dataStatus.map((entry, idx) => (
                      <Cell key={`cell-${idx}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>

          {/* Bar: Active vs Inactive (Last 6 Months) */}
          <Col xs={24} lg={8}>
            <Card
              title="User Activity Overview"
              style={{
                borderRadius: 16,
                background: "linear-gradient(0deg, rgb(238 209 238 / 54%) 80.26%, rgba(250, 245, 250, 0) 143.39%, rgb(245 235 245 / 44%) 143.39%)",
                border: "1px solid rgba(0,0,0,0.04)",
              }}
              bodyStyle={{ height: 300 }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={dataLast6}
                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="1" />
                  <XAxis dataKey="name" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Active" fill="#10b981">
                    <LabelList dataKey="Active" position="top" />
                  </Bar>
                  <Bar dataKey="Inactive" fill="#ef4444">
                    <LabelList dataKey="Inactive" position="top" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>

          {/* Bar: New Users (This vs Previous Month) */}
          <Col xs={24} lg={8}>
            <Card
              title="User Registrations"
              style={{
                borderRadius: 16,
                background: "linear-gradient(0deg, rgb(238 209 238 / 54%) 80.26%, rgba(250, 245, 250, 0) 143.39%, rgb(245 235 245 / 44%) 143.39%)",
                border: "1px solid rgba(0,0,0,0.04)",
              }}
              bodyStyle={{ height: 300 }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={dataNewUsers}
                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="This Month" fill="#6366f1">
                    <LabelList dataKey="This Month" position="top" />
                  </Bar>
                  <Bar dataKey="Previous Month" fill="#a78bfa">
                    <LabelList dataKey="Previous Month" position="top" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

        {/* NEW: Leaderboards */}
        <Spin spinning={lbLoading}>
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} lg={12}>
              <Card
                title={
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <SlLike /> Top Liked Modules
                  </span>
                }
                style={{
                  borderRadius: 16,
                  background: cardBG,
                  border: "1px solid rgba(0,0,0,0.04)",
                }}
                bodyStyle={{ padding: 0 }}
              >
                <Table
                  size="small"
                  pagination={false}
                  columns={moduleCols}
                  dataSource={topModules}
                  rowKey="key"
                />
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card
                title={
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <FaUsersLine /> Top Coin Board
                  </span>
                }
                style={{
                  borderRadius: 16,
                  background: cardBG,
                  border: "1px solid rgba(0,0,0,0.04)",
                }}
                bodyStyle={{ padding: 0 }}
              >
                <Table
                  size="small"
                  pagination={false}
                  columns={userCols}
                  dataSource={topUsers}
                  rowKey="key"
                />
              </Card>
            </Col>
          </Row>
        </Spin>
      </Spin>
    </div>
  );
};

export default Dashboard;
